import { attachCartRecommendationAnalytics } from './bd-cart-recommendation-analytics-v76.js?v=d02818fbed7c';
import { prioritizeCartSources, selectCartRecommendations } from './bd-cart-recommendations-v74.js?v=780ef65bf257';

// Only versioned, cart-independent reviewed facts are shared between refreshes.
const bdCartReviewedFacts = new Map();
function bdCartRequest(url, signal, format = 'text', timeout = 6000, cache = 'default') {
  const controller = new AbortController();
  let rejectCancelled;
  const abort = () => { controller.abort(); rejectCancelled?.(new Error('Cart refresh cancelled')); };
  if (signal?.aborted) return Promise.reject(new Error('Cart refresh cancelled'));
  const cancelled = new Promise((_, reject) => { rejectCancelled = reject; });
  signal?.addEventListener('abort', abort, { once: true });
  let timer;
  const deadline = new Promise((_, reject) => {
    timer = setTimeout(() => { controller.abort(); reject(new Error('Cart request timed out')); }, timeout);
  });
  const response = fetch(url, { signal: controller.signal, cache }).then(async result => {
    if (!result.ok) throw new Error('Cart response unavailable');
    return format === 'json' ? result.json() : result.text();
  });
  return Promise.race([response, deadline, cancelled]).finally(() => {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  });
}
function bdCartFacts(url, embedded) {
  if (embedded?.version === 'v76' && embedded.products && typeof embedded.products === 'object' && !Array.isArray(embedded.products)) {
    const facts = Promise.resolve(embedded.products);
    if (url) bdCartReviewedFacts.set(url, facts);
    return facts;
  }
  if (!url) return Promise.resolve({});
  if (!bdCartReviewedFacts.has(url)) {
    const request = bdCartRequest(url, null, 'json', 4000, 'force-cache').then(data => {
      if (data.version !== 'v76' || !data.products || typeof data.products !== 'object' || Array.isArray(data.products)) throw new Error('Invalid reviewed facts');
      return data.products;
    }).catch(() => { bdCartReviewedFacts.delete(url); return {}; });
    bdCartReviewedFacts.set(url, request);
  }
  return bdCartReviewedFacts.get(url);
}

// Purchase patterns only influence a later selection within approved pools.
// Never retain cart HTML, stock or prices, and never await these signals to paint.
const bdCartPurchaseSignals = new Map();
const bdCartSignalTTL = 5 * 60 * 1000;
function bdCartCacheSignals(root, sourceId, products) {
  const signals = [];
  (Array.isArray(products) ? products : []).slice(0, 10).forEach((product, index) => {
    if (!/^[1-9]\d*$/.test(String(product?.id))) return;
    let url;
    try { url = new URL(product.url, window.location.origin); } catch { return; }
    if (url.origin !== window.location.origin || !url.pathname.startsWith(`${root}products/`) || url.searchParams.get('pr_prod_strat') !== 'copurchase') return;
    signals.push({ id: String(product.id), rank: index + 1, url: url.href });
  });
  const key = `${root}:${sourceId}`;
  bdCartPurchaseSignals.delete(key);
  bdCartPurchaseSignals.set(key, { expires: Date.now() + bdCartSignalTTL, signals });
  while (bdCartPurchaseSignals.size > 24) bdCartPurchaseSignals.delete(bdCartPurchaseSignals.keys().next().value);
  return signals;
}
function bdCartApplySignals(root, sourceIds, candidates) {
  sourceIds.forEach(sourceId => {
    const key = `${root}:${sourceId}`;
    const cached = bdCartPurchaseSignals.get(key);
    if (!cached) return;
    if (cached.expires <= Date.now()) { bdCartPurchaseSignals.delete(key); return; }
    cached.signals.forEach(signal => {
      candidates.filter(item => item.ruleId && String(item.id) === signal.id).forEach(item => {
        if (!item.copurchaseRank || item.copurchaseRank > signal.rank) {
          item.copurchaseRank = signal.rank;
          item.recommendationUrl = signal.url;
        }
      });
    });
  });
}

class BDCartComplements extends HTMLElement {
  connectedCallback() {
    this.cardSignatures ||= new WeakMap();
    this.staleControls ||= new Map();
    this.stopAnalytics = attachCartRecommendationAnalytics(this);
    this.onStaleAction = event => {
      if (this.refreshing && event.target.closest?.('[data-recommendation-items]')) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this.addEventListener('click', this.onStaleAction, true);
    this.addEventListener('submit', this.onStaleAction, true);
    this.onPageShow = event => { if (event.persisted) this.refresh(); };
    window.addEventListener('pageshow', this.onPageShow);
    this.onFocusIntent = () => { if (this.refreshing) this.refreshFocus = null; };
    document.addEventListener('pointerdown', this.onFocusIntent, true);
    document.addEventListener('keydown', this.onFocusIntent, true);
    this.refresh({ initial: !this.hasConnected });
    this.hasConnected = true;
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, event => this.refresh({ event }));
    }
  }
  disconnectedCallback() {
    this.stopAnalytics?.();
    this.controller?.abort();
    this.controller = null;
    this.unsubscribe?.();
    window.removeEventListener('pageshow', this.onPageShow);
    document.removeEventListener('pointerdown', this.onFocusIntent, true);
    document.removeEventListener('keydown', this.onFocusIntent, true);
    this.removeEventListener('click', this.onStaleAction, true);
    this.removeEventListener('submit', this.onStaleAction, true);
    this.setRefreshing(false);
  }
  json(document, selector) {
    try { return JSON.parse(document.querySelector(selector)?.textContent || 'null'); }
    catch { return null; }
  }
  validCart(cart) {
    return cart && !cart.errors && Array.isArray(cart.items) && Number.isInteger(cart.item_count) && cart.item_count >= 0 &&
      typeof cart.requires_shipping === 'boolean' && cart.items.every(item => item.product_id);
  }
  setRefreshing(refreshing) {
    this.refreshing = refreshing;
    if (refreshing) {
      this.setAttribute('aria-busy', 'true');
      if (!this.hidden) this.style.minHeight = `${this.getBoundingClientRect().height}px`;
      this.querySelectorAll('[data-recommendation-items] button, [data-recommendation-items] a[href]').forEach(control => {
        if (this.staleControls.has(control)) return;
        this.staleControls.set(control, { disabled: control.disabled, aria: control.getAttribute('aria-disabled') });
        if (control.tagName === 'BUTTON') control.disabled = true;
        control.setAttribute('aria-disabled', 'true');
      });
    } else {
      this.removeAttribute('aria-busy');
      this.style.minHeight = '';
      this.staleControls.forEach((state, control) => {
        if (control.tagName === 'BUTTON') control.disabled = state.disabled;
        if (state.aria === null) control.removeAttribute('aria-disabled');
        else control.setAttribute('aria-disabled', state.aria);
      });
      this.staleControls.clear();
      this.refreshFocus = null;
    }
  }
  clearOffers() {
    const restoreFocus = this.refreshFocus && (document.activeElement === document.body || document.activeElement === this.refreshFocus.element);
    this.hidden = true;
    this.cartIdentity = null;
    this.querySelector('[data-recommendation-items]')?.replaceChildren();
    this.setRefreshing(false);
    if (restoreFocus) document.getElementById('checkout')?.focus({ preventScroll: true });
  }
  extendOffers(cartProducts, rules, candidates, locked = []) {
    if (!locked.length) return selectCartRecommendations(cartProducts, rules, candidates, 2);
    if (locked.length >= 2) return locked;
    // The original selector checks a replacement against the locked card,
    // including current stock, compatibility and combo/included families.
    const additions = candidates.flatMap(item => selectCartRecommendations(cartProducts, rules, [item], 1)).sort((a, b) => b.score - a.score);
    for (const addition of additions) {
      const pair = selectCartRecommendations(cartProducts, rules, [...locked, addition], 2);
      if (pair.length === 2 && locked.every(item => pair.some(match => String(match.id) === String(item.id)))) {
        return [...locked, pair.find(item => String(item.id) !== String(locked[0].id))];
      }
    }
    return locked;
  }
  renderOffers(selected) {
    const target = this.querySelector('[data-recommendation-items]');
    if (!target) return;
    const previous = [...target.children];
    const restoreFocus = this.refreshFocus && (document.activeElement === document.body || document.activeElement === this.refreshFocus.element);
    const active = target.contains(document.activeElement) ? document.activeElement : restoreFocus ? this.refreshFocus.element : null;
    const activeId = active?.closest('[data-recommendation-id]')?.dataset.recommendationId;
    // Retain existing order for identities that are still eligible. New stock,
    // prices or variant forms replace the old markup before it becomes usable.
    const previousIds = previous.map(element => element.dataset.recommendationId);
    selected = [...selected].sort((a, b) => {
      const ai = previousIds.indexOf(String(a.id)), bi = previousIds.indexOf(String(b.id));
      return (ai < 0 ? previous.length : ai) - (bi < 0 ? previous.length : bi);
    });
    selected.forEach((item, index) => {
      const signature = this.cardSignatures.get(item.element) || item.element.outerHTML;
      const existing = previous.find(element => element.dataset.recommendationId === String(item.id));
      if (existing && this.cardSignatures.get(existing) === signature) item.element = existing;
      this.cardSignatures.set(item.element, signature);
      item.element.dataset.sourceId = item.sourceId;
      item.element.dataset.position = String(index + 1);
      const reason = item.element.querySelector('[data-recommendation-reason]');
      if (reason) { reason.textContent = item.reason; reason.hidden = !item.reason; }
      if (item.recommendationUrl) item.element.querySelectorAll('a[href]').forEach(link => { link.href = item.recommendationUrl; });
    });
    const elements = selected.map(item => item.element);
    previous.forEach(element => { if (!elements.includes(element)) element.remove(); });
    elements.forEach((element, index) => { if (target.children[index] !== element) target.insertBefore(element, target.children[index] || null); });
    this.hidden = selected.length === 0;
    this.setRefreshing(false);
    if (active && (!active.isConnected || document.activeElement === document.body)) {
      const replacement = elements.find(element => element.dataset.recommendationId === activeId);
      (active.isConnected ? active : replacement?.querySelector('button, a[href]') || document.getElementById('checkout'))?.focus({ preventScroll: true });
    }
    return selected;
  }
  async refresh({ initial = false, event } = {}) {
    this.controller?.abort();
    const controller = new AbortController();
    this.controller = controller;
    const current = () => this.controller === controller && !controller.signal.aborted && this.isConnected;
    const target = this.querySelector('[data-recommendation-items]');
    const active = target?.contains(document.activeElement) ? document.activeElement : null;
    // Disabling a focused native button can blur it to body before validation.
    // A later pointer/keyboard action cancels this intent so we never steal focus.
    this.refreshFocus = active ? { element: active } : this.refreshing && document.activeElement === document.body ? this.refreshFocus : null;
    this.setRefreshing(true);
    let rendered = false;
    try {
      const root = window.Shopify.routes.root;
      const embeddedCart = initial ? this.json(this, '[data-cart-state]') : null;
      const embeddedSnapshot = initial ? this.json(this, '[data-cart-rule-data]') : null;
      const useEmbedded = this.validCart(embeddedCart) && embeddedSnapshot && Array.isArray(embeddedSnapshot.cart) && Array.isArray(embeddedSnapshot.rules);
      const eventCart = this.validCart(event?.cartData) ? event.cartData : null;
      const sectionUrl = new URL(this.dataset.cartUrl || `${root}cart`, window.location.origin);
      sectionUrl.searchParams.set('section_id', this.dataset.sectionId);
      const bundledSection = eventCart?.sections?.[this.dataset.sectionId];
      const [cartResult, sectionResult, factsResult] = await Promise.allSettled([
        useEmbedded ? Promise.resolve(embeddedCart) : eventCart ? Promise.resolve(eventCart) : bdCartRequest(`${root}cart.js`, controller.signal, 'json'),
        useEmbedded ? Promise.resolve(this) : typeof bundledSection === 'string' ? Promise.resolve(new DOMParser().parseFromString(bundledSection, 'text/html')) :
          bdCartRequest(sectionUrl, controller.signal).then(html => new DOMParser().parseFromString(html, 'text/html')),
        bdCartFacts(this.dataset.factsUrl, this.json(this, '[data-cart-reviewed-facts]')),
      ]);
      if (!current()) return;
      if (cartResult.status !== 'fulfilled' || !this.validCart(cartResult.value)) { this.clearOffers(); return; }
      const cart = cartResult.value.items;
      this.stopAnalytics?.confirm?.(cart);
      if (!cart.length) { this.clearOffers(); return; }
      const cartIds = new Set(cart.map(item => String(item.product_id)));
      const freshSection = sectionResult.status === 'fulfilled' ? sectionResult.value : null;
      const snapshot = this.json(freshSection, '[data-cart-rule-data]');
      if (snapshot) {
        if (!Array.isArray(snapshot.cart) || !Array.isArray(snapshot.rules) || snapshot.cart.some(item => !item.id)) { this.clearOffers(); return; }
        const factIds = new Set(snapshot.cart.map(item => String(item.id)));
        if (factIds.size !== cartIds.size || [...cartIds].some(id => !factIds.has(id))) { this.clearOffers(); return; }
      } else if (this.dataset.ruleRefresh === 'true') { this.clearOffers(); return; }
      const reviewed = factsResult.status === 'fulfilled' ? factsResult.value : {};
      const withReviewedFacts = product => {
        const facts = reviewed[String(product.id)] || {};
        const merged = { ...product };
        for (const key of ['interfaces', 'compatibilityKeys', 'includedFamilies']) {
          merged[key] = [...new Set([...(Array.isArray(product[key]) ? product[key] : []), ...(Array.isArray(facts[key]) ? facts[key] : [])])];
        }
        for (const key of ['screenMin', 'screenMax']) if (Number(facts[key]) > 0) merged[key] = facts[key];
        if (!merged.family && typeof facts.family === 'string') merged.family = facts.family;
        return merged;
      };
      const cartProducts = (snapshot?.cart || cart.map(item => ({ ...item, id: item.product_id, title: item.product_title || item.title || '', category: item.product_type || '' }))).map(withReviewedFacts);
      const rules = snapshot?.rules || [];
      const sourceIds = prioritizeCartSources(cartProducts, rules);
      const ruleCards = [...(freshSection?.querySelector('[data-cart-rule-cards]')?.content?.querySelectorAll('[data-recommendation-id]') || [])];
      const candidate = (element, sourceId = '') => {
        let facts = {};
        try { facts = JSON.parse(element.querySelector('[data-product-facts]')?.textContent || '{}'); } catch { /* Unknown facts never establish compatibility. */ }
        return withReviewedFacts({ ...facts,
          id: element.dataset.recommendationId, available: element.dataset.available === 'true',
          title: element.dataset.productTitle || '', family: element.dataset.family || facts.family || '',
          ruleId: element.dataset.ruleId || '', sourceId, element,
        });
      };
      const candidates = ruleCards.map(element => candidate(element));
      if (this.dataset.purchaseSignals === 'true') bdCartApplySignals(root, sourceIds, candidates);
      const identity = JSON.stringify(cart.map(item => [String(item.product_id), String(item.variant_id || item.id || '')]).sort((a, b) => a.join(':').localeCompare(b.join(':'))));
      const sameCart = this.cartIdentity === identity;
      this.cartIdentity = identity;
      const locked = [];
      if (sameCart) {
        // Quantity updates preserve identities/order only after their fresh
        // candidate forms pass the same availability and compatibility checks.
        for (const element of this.querySelector('[data-recommendation-items]')?.children || []) {
          const item = candidates.find(candidate => String(candidate.id) === element.dataset.recommendationId);
          if (!item) continue;
          const eligible = selectCartRecommendations(cartProducts, rules, [...locked, item], 2);
          if (eligible.length === locked.length + 1 && locked.every(previous => eligible.some(match => String(match.id) === String(previous.id)))) {
            locked.push(eligible.find(match => String(match.id) === String(item.id)));
          }
        }
      }
      // Current merchant offers are ready in Liquid; cached purchase patterns
      // rank new/replacement cards without displacing an eligible visible pair.
      let selected = this.renderOffers(this.extendOffers(cartProducts, rules, candidates, locked)) || [];
      rendered = true;
      const results = await Promise.allSettled(sourceIds.map(async sourceId => {
        const url = new URL(this.dataset.url, window.location.origin);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('product_id', sourceId);
        url.searchParams.set('intent', 'complementary');
        url.searchParams.set('limit', '10');
        const html = await bdCartRequest(url, controller.signal, 'text', 2500);
        const result = new DOMParser().parseFromString(html, 'text/html');
        return [...result.querySelectorAll('[data-recommendation-items] > [data-recommendation-id]')].map(element => candidate(element, sourceId));
      }));
      if (!current()) return;
      candidates.push(...results.flatMap(result => result.status === 'fulfilled' ? result.value : []));
      if (this.dataset.purchaseSignals === 'true') bdCartApplySignals(root, sourceIds, candidates);
      if (selected.length < 2) selected = this.renderOffers(this.extendOffers(cartProducts, rules, candidates, selected)) || [];
      if (this.dataset.purchaseSignals === 'true' && selected.some(item => item.ruleId)) {
        const signals = await Promise.allSettled(sourceIds.map(async sourceId => {
          const url = new URL(`${root}recommendations/products.json`, window.location.origin);
          url.searchParams.set('product_id', sourceId);
          url.searchParams.set('intent', 'related');
          url.searchParams.set('limit', '10');
          const data = await bdCartRequest(url, controller.signal, 'json', 2500);
          return Array.isArray(data.products) ? data.products : [];
        }));
        if (!current()) return;
        // Keep actual Shopify attribution for matching visible offers without
        // changing their identity, order or the controls beneath the pointer.
        signals.forEach((result, index) => {
          if (result.status !== 'fulfilled') return;
          bdCartCacheSignals(root, sourceIds[index], result.value).forEach(signal => {
            selected.filter(item => item.ruleId && String(item.id) === signal.id).forEach(item => {
              item.element.querySelectorAll('a[href]').forEach(link => { link.href = signal.url; });
            });
          });
        });
      }
    } catch { if (current() && !rendered) this.clearOffers(); }
    finally { if (current()) this.setRefreshing(false); }
  }
}

if (!customElements.get('bd-cart-complements')) customElements.define('bd-cart-complements', BDCartComplements);
